import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/useToast';
import { useI18n } from '@/hooks/useI18n';
import { useAuth } from '@/hooks/useAuth';
import type { TranslationKey } from '@/types/i18n.types';

/** Codici d'errore di close_travel_request, ciascuno con il suo messaggio tradotto. */
const CLOSE_REQUEST_ERRORS = [
  'travel_request_has_payments',
  'travel_request_payment_in_flight',
  'travel_request_completed',
  'forbidden',
] as const;

/**
 * Azioni sulle travel request della pagina richieste. Non tengono dati: a operazione
 * riuscita chiamano `onChanged`, e chi possiede le liste le ricarica.
 */
export const useTravelRequestActions = (onChanged: () => void) => {
  const { t } = useI18n();
  const { user } = useAuth();

  const fail = useCallback((key: TranslationKey) => {
    toast({
      title: String(t('common.error')),
      description: String(t(key)),
      variant: 'destructive',
    });
    return false;
  }, [t]);

  const succeed = useCallback((key: TranslationKey) => {
    onChanged();
    toast({ variant: 'success', title: String(t('common.success')), description: String(t(key)) });
    return true;
  }, [onChanged, t]);

  // Il consulente prende in carico una richiesta che gli e' stata proposta.
  const acceptTravelRequest = useCallback(async (requestId: string) => {
    if (!user?.id) return fail('errors.generic');
    const { error } = await supabase
      .from('travel_requests')
      .update({ status: 'assigned', updated_at: new Date().toISOString(), assigned_designer_id: user.id })
      .eq('id', requestId);
    return error ? fail('errors.generic') : succeed('travelRequest.acceptSuccess');
  }, [user?.id, fail, succeed]);

  // Assegna una travel request ad un travel designer, in modo definitivo sia per l'admin
  // sia per l'agency: il membro a cui l'agenzia gira la richiesta non deve accettarla,
  // trova subito "Crea itinerario". Il passaggio pending -> assigned fa partire dal
  // trigger la mail travel_request_accepted_by_designer al cliente.
  const assignTravelRequest = useCallback(async (requestId: string, designerId: string) => {
    const { error } = await supabase
      .from('travel_requests')
      .update({ status: 'assigned', assigned_designer_id: designerId, updated_at: new Date().toISOString() })
      .eq('id', requestId);
    // travelRequestMessages esiste nei json ma non e' fra i namespace tipizzati.
    return error
      ? fail('travelRequestMessages.assignError' as TranslationKey)
      : succeed('travelRequestMessages.assignSuccess' as TranslationKey);
  }, [fail, succeed]);

  // Chiude una travel request (stato 'rejected'). Vale sia per il "rifiuta" dell'advisor
  // sia per il "chiudi richiesta" di staff e agenzia: la RPC controlla i permessi, rifiuta
  // se la richiesta e' stata pagata, porta a 'expired' le proposte aperte e ne annulla le
  // rate, cosi' il cliente non puo' piu' pagarle e i solleciti si fermano.
  const closeTravelRequest = useCallback(async (requestId: string) => {
    const { error } = await supabase.rpc(
      'close_travel_request' as never,
      { p_travel_request_id: requestId } as never
    );
    if (error) {
      const code = CLOSE_REQUEST_ERRORS.find((c) => error.message?.includes(c));
      return fail(code ? (`travelRequest.closeErrors.${code}` as TranslationKey) : 'errors.generic');
    }
    return succeed('travelRequest.closeSuccess');
  }, [fail, succeed]);

  return { acceptTravelRequest, assignTravelRequest, closeTravelRequest };
};
